import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { environment } from "@env";
import {
  ApproveBudgetDTO,
  BudgetDTO,
  NewFlowApproveBudgetDTO
} from "@shared/models/budget-dto";
import { BudgetAiItemDetailDTO } from "@shared/models/budget-ai-item-detail-dto";
import { SearchFormDTO } from "src/app/modules/private/modules/service-request-pending-approval/models/search-form-dto";
import { BudgetApprovalQueueDTO } from "@shared/models/budget-approval-queue-dto";
import { retry } from "rxjs/operators";
import { MarginsValues } from "src/app/modules/private/modules/service-request/components/components/budget/models/margins-values";
import { BudgetQuestionDTO } from "@shared/models/budget-question-dto";
import { DISABLED_NOTIFY_MESSAGE } from "@core/constants";
import { BudgetMaintenanceProposal, ClientDisapprovalDTO } from "@core/dtos";
import { BudgetStatusEnum } from "@shared/enums/budget-status";

@Injectable({
  providedIn: "root"
})
export class BudgetService {
  private budgetsUrl = "budgets/";
  private headers = new HttpHeaders({
    [DISABLED_NOTIFY_MESSAGE]: "true"
  });

  constructor(private http: HttpClient) {}

  private mapToBlobCategory(category: string): string {
    const c = (category || "").toUpperCase();
    // Legacy internal codes → new API values
    if (
      [
        "CATEGORY_1",
        "1",
        "CATEGORY_VEHICLE_ATTENDANCE",
        "CATEGORY_VEHICLE_ATTENDANCE_EVIDENCE"
      ].includes(c)
    ) {
      return "CATEGORY_VEHICLE_ATTENDANCE_EVIDENCE";
    }
    if (["CATEGORY_3", "3", "CATEGORY_SERVICE_FINISHED_EVIDENCE"].includes(c)) {
      return "CATEGORY_SERVICE_FINISHED_EVIDENCE";
    }
    if (
      [
        "CATEGORY_2",
        "2",
        "CATEGORY_SERVICE_WEAR_EVIDENCE",
        "VEHICLE_DAMAGE_EVIDENCE"
      ].includes(c)
    ) {
      return "CATEGORY_SERVICE_WEAR_EVIDENCE";
    }
    return "CATEGORY_SERVICE_WEAR_EVIDENCE";
  }

  private baseUrl(budgetId: number) {
    return `${environment.apiUrl}${this.budgetsUrl}${budgetId}`;
  }

  findById(id: number, includeDefaultPrices?: boolean): Observable<BudgetDTO> {
    return this.http.get<BudgetDTO>(
      `${environment.apiUrl}${this.budgetsUrl}${id}?includeDefaultPrices=${includeDefaultPrices}`
    );
  }

  findApprovalQueue(
    filters?: SearchFormDTO
  ): Observable<BudgetApprovalQueueDTO[]> {
    let query = "?";
    if (filters) {
      if (filters.requestId) {
        query += `&requestId=${filters.requestId}`;
      }
      if (filters.clientId) {
        query += `&clientId=${filters.clientId}`;
      }
      if (filters.carWorkshopId) {
        query += `&carWorkshopId=${filters.carWorkshopId}`;
      }
    }
    return this.http.get<BudgetApprovalQueueDTO[]>(
      `${environment.apiUrl}${this.budgetsUrl}approval-queue${query}`
    );
  }

  approveBudgets(budgetsId: number[]): Observable<BudgetDTO[]> {
    const data = [];
    budgetsId.forEach((item) => data.push({ id: item }));
    return this.http.put<BudgetDTO[]>(
      `${environment.apiUrl}${this.budgetsUrl}approves`,
      data
    );
  }

  update(id: number, data: BudgetDTO): Observable<BudgetDTO> {
    return this.http.put<BudgetDTO>(
      `${environment.apiUrl}${this.budgetsUrl}${id}`,
      data
    );
  }

  updateIntermediary(id: number, data: BudgetDTO): Observable<BudgetDTO> {
    return this.http.put<BudgetDTO>(
      `${environment.apiUrl}${this.budgetsUrl}${id}/intermediary`,
      data
    );
  }

  updateDenyApproval(
    id: number,
    data: BudgetDTO,
    comments: string
  ): Observable<BudgetDTO> {
    const query = `?comments=${comments}`;
    return this.http.put<BudgetDTO>(
      `${environment.apiUrl}${this.budgetsUrl}${id}/noadminapproval${query}`,
      data
    );
  }

  updateDontNegotiate(id: number, data: BudgetDTO): Observable<BudgetDTO> {
    return this.http.put<BudgetDTO>(
      `${environment.apiUrl}${this.budgetsUrl}${id}/no-backoffice-negotiation`,
      data
    );
  }

  approve(id: number, data: ApproveBudgetDTO) {
    return this.http.put<any>(
      `${environment.apiUrl}${this.budgetsUrl}${id}/approve`,
      data,
      {
        headers: this.headers
      }
    );
  }

  //images
  getImages(id): Observable<any> {
    return this.http
      .get<any>(environment.apiUrl + `${this.budgetsUrl}${id}/files`, {
        headers: this.headers
      })
      .pipe(retry(1));
  }

  getImageFile(budgetId, id): Observable<any> {
    const requestOptions: Object = {
      responseType: "text"
    };
    return this.http
      .get<any>(
        environment.apiUrl + `${this.budgetsUrl}${budgetId}/files/${id}/`,
        requestOptions
      )
      .pipe(retry(1));
  }

  //add image
  addImage(id, formdata): Observable<any> {
    const file = formdata.get("file") as File;
    const type = (formdata.get("type") as string) || "IMAGE";
    const name = (formdata.get("name") as string) || file?.name || "file";
    const description =
      (formdata.get("description") as string) ||
      file?.type ||
      "application/octet-stream";
    const blobCategory = this.mapToBlobCategory(
      (formdata.get("category") as string) || "CATEGORY_SERVICE_WEAR_EVIDENCE"
    );

    const params = new HttpParams()
      .set("type", type)
      .set("name", name)
      .set("description", description)
      .set("blobCategory", blobCategory);

    const body = new FormData();
    body.append("file", file);

    return this.http
      .post<any>(environment.apiUrl + `${this.budgetsUrl}${id}/files`, body, {
        headers: this.headers,
        params
      })
      .pipe(retry(1));
  }

  //Delete image
  deleteImg(budgetId, fileId) {
    return this.http
      .delete<any>(
        environment.apiUrl + `${this.budgetsUrl}${budgetId}/files/${fileId}`,
        {
          headers: this.headers
        }
      )
      .pipe(retry(1));
  }

  updateImageCategory(
    budgetId: number,
    fileId: number,
    category: string
  ): Observable<any> {
    const params = new HttpParams().set(
      "blobCategory",
      this.mapToBlobCategory(category)
    );

    return this.http
      .patch<any>(
        environment.apiUrl + `${this.budgetsUrl}${budgetId}/files/${fileId}`,
        null,
        {
          headers: this.headers,
          params
        }
      )
      .pipe(retry(1));
  }

  updateApprovedJustification(
    id: number,
    data: BudgetDTO,
    justification: string
  ): Observable<BudgetDTO> {
    const query = `?justification=${justification}`;
    return this.http.put<BudgetDTO>(
      environment.apiUrl +
        `${this.budgetsUrl}${id}/approved-justification${query}`,
      data
    );
  }

  hasVerificationItems(id: number): Observable<boolean> {
    return this.http.get<boolean>(
      environment.apiUrl + `${this.budgetsUrl}${id}/hasVerificationItems`
    );
  }

  hasVerificationItemsNotConfirmed(id: number): Observable<boolean> {
    return this.http.get<boolean>(
      environment.apiUrl +
        `${this.budgetsUrl}${id}/hasVerificationItemsNotConfirmed`
    );
  }

  saveCarWorkshopPdf(
    budgetId: number,
    requestId: string,
    file: File
  ): Observable<{ filename: string; filepath: string }> {
    let query = "?";
    query += `requestId=${requestId}`;

    const body = new FormData();
    body.append("file", file);

    const url = `${environment.apiUrl}${this.budgetsUrl}${budgetId}/car-workshop-pdf${query}`;

    return this.http.put<{ filename: string; filepath: string }>(url, body);
  }

  getCalculatedMargins(budgetId: number) {
    return this.http.get<MarginsValues>(
      `${environment.apiUrl}${this.budgetsUrl}${budgetId}/calculated-margins`
    );
  }

  addQuestionToBudget(budgetId: number, question: string) {
    return this.http.post<any>(
      `${environment.apiUrl}${this.budgetsUrl}${budgetId}/question`,
      question
    );
  }

  getBudgetQuestions(budgetId: number) {
    return this.http.get<BudgetQuestionDTO[]>(
      `${environment.apiUrl}${this.budgetsUrl}${budgetId}/question`
    );
  }

  clientApproval(
    budgetId: number,
    data?: NewFlowApproveBudgetDTO
  ): Observable<void> {
    return this.http.patch<void>(
      `${this.baseUrl(budgetId)}/events/client-approval`,
      data ?? {}
    );
  }

  clientTechApproval(budgetId: number): Observable<void> {
    return this.http.patch<void>(
      `${environment.apiUrl}${this.budgetsUrl}${budgetId}/tech-approve`,
      {}
    );
  }

  clientDisapproval(
    budgetId: number,
    reasons: ClientDisapprovalDTO
  ): Observable<void> {
    const payload = {
      ...reasons,
      reasons: Array.isArray(reasons.reasons)
        ? reasons.reasons
        : reasons.reasons
        ? [reasons.reasons]
        : []
    };

    return this.http.patch<void>(
      `${this.baseUrl(budgetId)}/events/client-disapproval`,
      payload
    );
  }

  downloadTxtFile(budgetId: number) {
    return this.http.post(
      `${environment.apiUrl}budgets/${budgetId}/export`,
      null,
      {
        observe: "response",
        responseType: "blob" as "json"
      }
    );
  }

  getWorkshopMaintenanceProposals(
    budgetId: number
  ): Observable<BudgetMaintenanceProposal> {
    return this.http.get<BudgetMaintenanceProposal>(
      `${environment.apiUrl}${this.budgetsUrl}${budgetId}/workshop-maintenance-proposals`
    );
  }

  getAiItemDetail(itemId: number): Observable<BudgetAiItemDetailDTO> {
    return this.http.get<BudgetAiItemDetailDTO>(
      `${environment.apiUrl}budget-ai/item/${itemId}`
    );
  }

  approveWorkshopMaintenanceProposal(
    proposalId: number,
    data?: NewFlowApproveBudgetDTO
  ): Observable<void> {
    const baseUrl = `${environment.apiUrl}workshop-maintenance-proposals`;
    const url = `${baseUrl}/${proposalId}/events/accepted-by-client`;

    return this.http.patch<void>(url, data ?? {});
  }

  calculateCustomPrice(budgetDTO: BudgetDTO): Observable<BudgetDTO> {
    return this.http.patch<BudgetDTO>(
      `${environment.apiUrl}fee/custom-fee`,
      budgetDTO ?? {}
    );
  }

  findByIdFee(id: number): Observable<BudgetDTO> {
    return this.http.get<BudgetDTO>(
      `${environment.apiUrl}${this.budgetsUrl}${id}`
    );
  }
}
